/// <reference types="@angular/localize" />

import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_INITIALIZER, enableProdMode, ErrorHandler, importProvidersFrom, Injector } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DateAdapter, MAT_DATE_FORMATS } from '@angular/material/core';
import { bootstrapApplication } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideRouter, Router } from '@angular/router';
import { VersionCheckService } from '@core/services/version-check.service';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { provideTranslateService } from '@ngx-translate/core';
import { provideTranslateHttpLoader } from '@ngx-translate/http-loader';
import * as Sentry from '@sentry/angular-ivy';
import { APP_DATE_FORMATS, AppDateAdapter } from '@shared/date.adapter';
import { IconsFactory } from '@shared/services/icon/icon.service';
import { AuthConfig, OAuthStorage, provideOAuthClient } from 'angular-oauth2-oidc';
import player from 'lottie-web';
import { RECAPTCHA_V3_SITE_KEY } from 'ng-recaptcha-2';
import { CookieService } from 'ngx-cookie-service';
import { provideLottieOptions } from 'ngx-lottie';
import { provideEnvironmentNgxMask } from 'ngx-mask';
import { AppComponent } from './app/app.component';
import { routes } from './app/app.routes';
import { AuthGuardService } from './app/core/auth-guard.service';
import { authCodeFlowConfig } from './app/core/auth.config';
import { AuthInterceptor } from './app/core/auth.interceptor';
import { OnboardedGuardService } from './app/core/onboarded-guard.service';
import { HTTPListener } from './app/core/progress.inteceptor';
import { PropertiesGuardService } from './app/core/properties-guard.service';
import { PropertiesOrOnboardedGuardService } from './app/core/properties-or-onboarded-guard.service';
import { CustomErrorHandler } from './app/core/services/CustomErrorHandler';
import { TreezorSessionKeepaliveInterceptor } from './app/core/treezor-session-keepalive.interceptor';
import { WindowRef } from './app/user/components/registration/WindowRef';
import { environment } from './environments/environment';

if (environment.production) {
  enableProdMode();
}

export function storageFactory(): OAuthStorage {
  return localStorage;
}

bootstrapApplication(AppComponent, {
  providers: [
    provideRouter(routes),
    provideOAuthClient({
      resourceServer: {
        allowedUrls: [environment.apiBaseUrl],
        sendAccessToken: true,
      },
    }),
    provideTranslateService({
      fallbackLang: 'en',
      loader: provideTranslateHttpLoader({
        prefix: './assets/i18n_translations/',
        suffix: '.json',
      }),
    }),
    importProvidersFrom(FontAwesomeModule, FormsModule, ReactiveFormsModule),
    { provide: AuthConfig, useValue: authCodeFlowConfig },
    { provide: OAuthStorage, useFactory: storageFactory },
    { provide: DateAdapter, useClass: AppDateAdapter },
    { provide: MAT_DATE_FORMATS, useValue: APP_DATE_FORMATS },
    WindowRef,
    AuthGuardService,
    OnboardedGuardService,
    PropertiesGuardService,
    PropertiesOrOnboardedGuardService,
    CookieService,
    { provide: HTTP_INTERCEPTORS, useClass: HTTPListener, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: TreezorSessionKeepaliveInterceptor, multi: true },
    { provide: 'BASE_API_URL', useValue: environment.apiBaseUrl },
    { provide: 'BASE_AI_URL', useValue: environment.aiApiBaseUrl },
    { provide: 'BASE_HUBS_URL', useValue: environment.hubsBaseUrl },
    { provide: Window, useValue: window },
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    { provide: Sentry.TraceService, deps: [Router] },
    { provide: RECAPTCHA_V3_SITE_KEY, useValue: environment.recaptcha.siteKey },
    {
      provide: APP_INITIALIZER,
      useFactory: () => () => {},
      deps: [Sentry.TraceService],
      multi: true,
    },
    provideAnimations(),
    provideLottieOptions({ player: () => player }),
    provideEnvironmentNgxMask(),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: APP_INITIALIZER,
      useFactory: (versionCheckService: VersionCheckService) => () => versionCheckService.initialize(),
      deps: [VersionCheckService],
      multi: true,
    },
    {
      provide: APP_INITIALIZER,
      useFactory: (injector: Injector) => {
        return () => injector.get(IconsFactory).init();
      },
      multi: true,
      deps: [Injector],
    },
  ],
}).catch(err => {
  let error = new Error('Error bootstrapping the application');
  console.error(error);
  console.error(err);
  throw error;
});
